/**
 * schoolYear.js — Cycle 62 · Feb 2026.
 *
 * Shared helpers to resolve the current school year (1..10) for a
 * given child so downstream surfaces (Maths Academy, Learn Topics,
 * Weekly Plan) don't have to re-implement the same fallback ladder.
 *
 * Fallback order:
 *   1. child.year explicitly set by the parent (source of truth)
 *   2. birth country school system + child.tier midpoint age
 *      → school-year lookup (e.g. India CBSE, US Common Core, UK)
 *   3. tier-only default (Seedlings=Y2, Explorers=Y5, Pioneers=Y8)
 *
 * The country → year mapping is deliberately small: we only tune the
 * "start age" that shifts a Year 1 kid to an earlier/later calendar
 * year in some systems (India starts formal schooling ~Class 1 at
 * age 6, US Kindergarten at 5, England Reception at 4-5). Anything
 * beyond that stays on the UK NC ladder which the app's frameworks
 * already speak natively.
 */

// Accept BOTH the legacy age-group ids (little/big/pro) and the current
// tier ids (seedlings/explorers/pioneers) so callers passing either shape
// resolve to the same sensible default. Prevents a Seedling being nudged
// to Year 4 because the tier key didn't match.
const TIER_DEFAULT_YEAR = {
  little: 2, big: 5, pro: 8,
  seedlings: 2, explorers: 5, pioneers: 8,
};
const TIER_DEFAULT_AGE = {
  little: 5, big: 8, pro: 12,
  seedlings: 5, explorers: 8, pioneers: 12,
};

// Rough "years above UK NC" offset per common school system.
// Positive → child would sit ONE year LOWER than UK NC for the same age
// (e.g. India starts Class 1 at age 6, so a 6yo who'd be UK Y2 is India
// Class 1 → offset -1). Kept intentionally small to avoid overfitting.
const COUNTRY_OFFSETS = {
  US: -1,    // US Grade 1 at age 6 (UK Y2 at age 6)
  IN: -1,    // India Class 1 at age 6
  IE: 0,     // Ireland aligns with UK
  AU: -1,    // Australia Year 1 at age 6
  NZ: -1,
  CA: -1,
  AE: -1,
  SG: -1,
};

function toYear(n) {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return null;
  return Math.max(1, Math.min(10, v));
}

/**
 * Return the school year (1..10) for a child, using every hint we have.
 * Never returns null — callers can trust a usable value.
 */
export function yearForChild(child) {
  if (!child) return 5;
  // 1) explicit year wins.
  const explicit = toYear(child.year);
  if (explicit) return explicit;

  // 2) birth country + tier-midpoint age → estimate.
  const tier = child.tier || "explorers";
  const midAge = TIER_DEFAULT_AGE[tier] ?? 8;
  const countryCode = String(child.birthCountry || "").toUpperCase();
  const offset = COUNTRY_OFFSETS[countryCode] ?? 0;
  // UK NC baseline: age 5 -> Y1, age 6 -> Y2, … age 14 -> Y10.
  const ukYear = toYear(midAge - 4);
  if (ukYear) return toYear(ukYear + offset) || ukYear;

  // 3) tier-only fallback.
  return TIER_DEFAULT_YEAR[tier] ?? 5;
}

/**
 * Suggested "starting year" chip to show in the Family wizard when
 * the parent hasn't picked yet. Same logic minus the explicit-year
 * short-circuit so we can nudge without silently overriding.
 */
export function suggestedYearForChild(child) {
  const clone = { ...(child || {}), year: null };
  return yearForChild(clone);
}
